import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "active", label: "Active", status: "active" },
  { key: "moderators", label: "Moderators", status: "active", role: "moderator" },
  { key: "pending", label: "Pending", status: "pending" },
  { key: "muted", label: "Muted", status: "muted" },
  { key: "banned", label: "Banned", status: "banned" },
];

/**
 * Community moderator roster: promote/demote, mute/ban, invite, approve/reject.
 */
export default function CommunityMembersPanel({
  communityId,
  accessToken,
  canChangeRoles: canChangeRolesProp = false,
  pendingCount = 0,
  onPendingCountChange,
  open: openProp,
  onOpenChange,
  triggerClassName,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;
  const setOpen = (v) => {
    if (!controlled) setInternalOpen(v);
    onOpenChange?.(v);
  };

  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canChangeRoles, setCanChangeRoles] = useState(
    Boolean(canChangeRolesProp)
  );
  const [busyId, setBusyId] = useState(null);
  const [inviteHandle, setInviteHandle] = useState("");
  const [inviting, setInviting] = useState(false);
  const [pendingBadge, setPendingBadge] = useState(pendingCount);
  const onPendingCountChangeRef = useRef(onPendingCountChange);
  onPendingCountChangeRef.current = onPendingCountChange;
  const pendingBadgeRef = useRef(pendingCount);
  pendingBadgeRef.current = pendingBadge;

  useEffect(() => {
    setPendingBadge(pendingCount);
  }, [pendingCount]);

  useEffect(() => {
    setCanChangeRoles(Boolean(canChangeRolesProp));
  }, [canChangeRolesProp]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!communityId || !accessToken) return;
    setLoading(true);
    try {
      const tabDef = TABS.find((t) => t.key === tab) || TABS[0];
      const params = { status: tabDef.status, limit: 100 };
      if (tabDef.role) params.role = tabDef.role;
      const res = await axios.get(
        `${API_BASE_URL}/social/communities/${communityId}/members`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        }
      );
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      if (res.data?.can_change_roles != null) {
        setCanChangeRoles(Boolean(res.data.can_change_roles));
      }
      const pc = Number(res.data?.pending_member_count);
      if (Number.isFinite(pc)) {
        setPendingBadge(pc);
        if (pc !== pendingBadgeRef.current) {
          pendingBadgeRef.current = pc;
          onPendingCountChangeRef.current?.(pc);
        }
      }
    } catch (err) {
      if (!silent) {
        toastError(
          err.response?.data?.message || err.message || "Failed to load members"
        );
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [communityId, accessToken, tab]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [open, load]);

  const patchMember = async (userId, body) => {
    setBusyId(userId);
    try {
        await axios.patch(
        `${API_BASE_URL}/social/communities/${communityId}/members/${encodeURIComponent(userId)}`,
        body,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Member updated");
      await load({ silent: true });
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not update member"
      );
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (userId) => {
    setBusyId(userId);
    try {
      await axios.post(
        `${API_BASE_URL}/social/communities/${communityId}/members/${encodeURIComponent(userId)}/approve`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Request approved");
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not approve"
      );
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (userId) => {
    setBusyId(userId);
    try {
      await axios.post(
        `${API_BASE_URL}/social/communities/${communityId}/members/${encodeURIComponent(userId)}/reject`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess("Request rejected");
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not reject"
      );
    } finally {
      setBusyId(null);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    const handle = inviteHandle.trim().replace(/^@/, "");
    if (!handle || inviting) return;
    setInviting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/social/communities/${communityId}/invites`,
        { handle },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      toastSuccess(`Invited @${handle}`);
      setInviteHandle("");
      setTab("active");
      await load();
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Could not invite"
      );
    } finally {
      setInviting(false);
    }
  };

  return (
    <>
      {!controlled ? (
        <Button
          type="button"
          variant="outline"
          className={cn(
            "border-current/40 bg-background/20 text-inherit hover:bg-background/30 hover:text-inherit",
            triggerClassName
          )}
          onClick={() => setOpen(true)}
        >
          <Users className="h-4 w-4 mr-1.5" />
          Members
          {pendingBadge > 0 ? (
            <Badge
              variant="secondary"
              className="ml-1.5 h-5 min-w-5 px-1 tabular-nums"
            >
              {pendingBadge}
            </Badge>
          ) : null}
        </Button>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Community members</DialogTitle>
          </DialogHeader>

          <form onSubmit={invite} className="flex gap-2 items-end shrink-0">
            <div className="flex-1 space-y-1">
              <Label htmlFor="invite-handle">Invite by handle</Label>
              <Input
                id="invite-handle"
                placeholder="@username"
                value={inviteHandle}
                onChange={(e) => setInviteHandle(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={inviting || !inviteHandle.trim()}>
              {inviting ? "…" : "Invite"}
            </Button>
          </form>

          <div className="flex gap-1 border-b border-border shrink-0">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors",
                  tab === t.key
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {t.label}
                {t.key === "pending" && pendingBadge > 0 ? (
                  <span className="ml-1 tabular-nums text-muted-foreground">
                    ({pendingBadge})
                  </span>
                ) : null}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-1 py-1">
            {loading && items.length === 0 ? (
              <p className="text-sm text-muted-foreground px-1 py-4">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground px-1 py-4">
                {tab === "moderators"
                  ? "No appointed moderators."
                  : "No members in this list."}
              </p>
            ) : (
              items.map((m) => {
                const uid = m.user_id;
                const user = m.user || {};
                const name =
                  user.display_name || user.username || uid?.slice(0, 8);
                const handle = user.username ? `@${user.username}` : null;
                const avatarSrc = resolveAvatarUrl(user.avatar_url);
                const busy = busyId === uid;
                const role = m.role;
                const status = m.status;

                return (
                  <div
                    key={m.membership_id || uid}
                    className="flex items-center gap-2 rounded-md px-2 py-2 hover:bg-muted/40"
                  >
                    <Avatar className="h-8 w-8 shrink-0">
                      {avatarSrc ? <AvatarImage src={avatarSrc} alt="" /> : null}
                      <AvatarFallback className="text-xs">
                        {(name || "?").slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {handle || uid}
                        {role === "moderator" ? (
                          <span className="ml-1.5 font-medium text-foreground">
                            Mod
                          </span>
                        ) : null}
                        {role === "owner" ? (
                          <span className="ml-1.5 font-medium text-foreground">
                            Owner
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end shrink-0">
                      {status === "pending" ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="h-7 text-xs"
                            disabled={busy}
                            onClick={() => approve(uid)}
                          >
                            Approve
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            disabled={busy}
                            onClick={() => reject(uid)}
                          >
                            Reject
                          </Button>
                        </>
                      ) : null}
                      {status === "active" &&
                      canChangeRoles &&
                      role !== "owner" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="h-7 text-xs"
                          disabled={busy}
                          onClick={() =>
                            patchMember(uid, {
                              role:
                                role === "moderator"
                                  ? "participant"
                                  : "moderator",
                            })
                          }
                        >
                          {role === "moderator" ? "Remove mod" : "Make mod"}
                        </Button>
                      ) : null}
                      {status === "active" && role !== "owner" ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            disabled={busy}
                            onClick={() =>
                              patchMember(uid, { status: "muted" })
                            }
                          >
                            Mute
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-destructive"
                            disabled={busy}
                            onClick={() =>
                              patchMember(uid, { status: "banned" })
                            }
                          >
                            Ban
                          </Button>
                        </>
                      ) : null}
                      {status === "muted" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="h-7 text-xs"
                          disabled={busy}
                          onClick={() =>
                            patchMember(uid, { status: "active" })
                          }
                        >
                          Unmute
                        </Button>
                      ) : null}
                      {status === "banned" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="h-7 text-xs"
                          disabled={busy}
                          onClick={() =>
                            patchMember(uid, { status: "active" })
                          }
                        >
                          Unban
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
